import { StoreWithProps, useClassState } from "@web-monorepo/hooks";
import type { Tool, ToolPayload } from "./EditableSetup";

export type UseEditableToolEditProps = {
  tool: Tool | null;
  createTool: (payload: ToolPayload) => void;
  editTool: (toolId: string, payload: ToolPayload) => void;
  deleteTool: (toolId?: string) => void;
  showTool: (payload: ToolPayload) => void;
  closeEditScreen: VoidFunction;
};

export type UseEditableToolEditResult = {
  payload: ToolPayload;
  disabled: boolean;
  /** True when the user is editing an already-saved tool (has an _id). */
  isEditingExisting: boolean;
  onEdit: (payload: ToolPayload, disabled?: boolean) => void;
  startNow: () => void;
  saveForLater: () => void;
  deleteCurrent: () => void;
};

/**
 * Valtio-backed store for EditableToolEdit's transient draft state. Holds
 * the in-progress payload and the form's disabled flag (driven by the
 * renderEditItem child component's `onEdit` callback). On save the payload
 * is dispatched to either createTool or editTool depending on whether the
 * existing tool has an `_id`.
 */
class EditableToolEditStore extends StoreWithProps<UseEditableToolEditProps> {
  payload: ToolPayload;
  disabled: boolean;

  constructor(props: UseEditableToolEditProps) {
    super(props);
    if (props.tool) {
      this.payload = props.tool.payload;
      this.disabled = false;
    } else {
      this.payload = {};
      this.disabled = true;
    }
  }

  get isEditingExisting(): boolean {
    return Boolean(this.props.tool && this.props.tool._id);
  }

  onEdit(payload: ToolPayload, disabled: boolean = false) {
    this.payload = payload;
    this.disabled = disabled;
  }

  startNow() {
    this.save();
    this.props.showTool(this.payload);
  }

  saveForLater() {
    this.save();
    this.props.closeEditScreen();
  }

  deleteCurrent() {
    this.props.deleteTool(this.props.tool?._id);
  }

  private save() {
    if (this.props.tool && this.props.tool._id) {
      this.props.editTool(this.props.tool._id, this.payload);
    } else if (this.payload) {
      this.props.createTool(this.payload);
    }
  }
}

export function useEditableToolEdit(props: UseEditableToolEditProps): UseEditableToolEditResult {
  const store = useClassState(EditableToolEditStore, props);

  return {
    payload: store.payload,
    disabled: store.disabled,
    isEditingExisting: store.isEditingExisting,
    onEdit: store.onEdit,
    startNow: store.startNow,
    saveForLater: store.saveForLater,
    deleteCurrent: store.deleteCurrent,
  };
}
